import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ReactiveFormsModule, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { RouterModule, Router } from '@angular/router';
import { UserInfoInterface } from '@core/models/authentication.model';
import { AuthenticationService } from '@core/services/authentication/authentication.service';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { TransactionSettingsService } from '@feature/dashboard/settings/transaction-settings/services/transaction-settings.service';
import { PartnerTransactionSettingsInterface } from '@feature/dashboard/settings/transaction-settings/types/transaction-settings';
import {
  GENDER,
  DOCUMENT_TYPE,
  DOCUMENT_SECTION,
} from '@feature/dashboard/senders/constant/upsert-sender.constant';
import { Store } from '@ngrx/store';
import { InputModule } from '@sharedModule/components/input/input.module';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { MaterialModule } from '@sharedModule/material/material.module';
import { CountryInfo } from '@sharedModule/models/country.model';
import { loadDestinationCountry } from '@sharedModule/store/destination-country/destination-country.actions';
import { selectDestinationCountry } from '@sharedModule/store/destination-country/destination-country.selector';
import { selectSourceCountry } from '@sharedModule/store/source-country/source-country.selector';
import { getCountryInfoFromCountryCode } from '@sharedModule/utils/array.utils';
import {
  getCreateTransactionForm,
  stripCalculatedFields,
  buildNewSenderPayload,
  buildChargesPayload,
  buildVelocityPayload,
} from '@sharedModule/utils/create-transaction.utils';
import { TransferDetailsComponent } from '@sharedModule/components/transfer-details/transfer-details.component';
import { pruneDeliveryDetails } from '@sharedModule/utils/transfer-details.utils';
import { DeliveryLookupService } from '@sharedModule/services/delivery/delivery-lookup.service';
import { Utils } from '@sharedModule/utils/util';
import { Observable, Subject, debounceTime, takeUntil } from 'rxjs';
import { TransactionService } from '../../services/transaction.service';
import {
  CreateTransactionInterface,
  TransactionChargesInterface,
} from '../../types/create-transaction';
import {
  ExistingUserComponent,
  ExistingUserDialogResult,
} from '../existing-user/existing-user.component';

@Component({
  selector: 'app-create-transaction',
  standalone: true,
  imports: [
    InputModule,
    CommonModule,
    RouterModule,
    MaterialModule,
    ReactiveFormsModule,
    TransferDetailsComponent,
  ],
  templateUrl: './create-transaction.component.html',
  styleUrl: './create-transaction.component.scss',
})
export class CreateTransactionComponent implements OnInit, OnDestroy {
  public readonly attrConstant = ATTRIBUTES_CONSTANT.transactions;
  public readonly gender = GENDER;
  public readonly documentType = DOCUMENT_TYPE;
  public readonly documentSection = DOCUMENT_SECTION;
  userInfo!: UserInfoInterface;
  createTxnFrom!: FormGroup;
  sourceCountryList!: Observable<CountryInfo[]>;
  destCountryList!: Observable<CountryInfo[]>;
  transactionSettings?: PartnerTransactionSettingsInterface;
  isExistingSender = false;
  isExistingBeneficiary = false;
  selectedSenderId: number | string = '';
  selectedBeneficiaryId: number | string = '';
  amountType = '';
  subject = new Subject<TransactionChargesInterface>();
  #destroy$ = new Subject<void>();

  get beneDetailsGroup(): FormGroup {
    return this.createTxnFrom.get('beneDetails') as FormGroup;
  }

  get senderDetailsGroup(): FormGroup {
    return this.createTxnFrom.get('senderDetails') as FormGroup;
  }

  constructor(
    private store: Store,
    private router: Router,
    private dialogRef: MatDialog,
    private utils: Utils,
    private deliveryLookup: DeliveryLookupService,
    private transactionService: TransactionService,
    private authenticationService: AuthenticationService,
    private messageHandlerService: MessageHandlerService,
    private transactionSettingsService: TransactionSettingsService
  ) {}

  ngOnInit(): void {
    this.userInfo = this.authenticationService.getUserInfo();
    this.createTxnFrom = getCreateTransactionForm(
      this.userInfo.mode === 'ONLINE'
    );
    const debounceTimeDuration = 1000;
    this.subject
      .pipe(debounceTime(debounceTimeDuration), takeUntil(this.#destroy$))
      .subscribe(res => {
        this.calculate(res);
      });
    this.loadData();
  }

  loadData(): void {
    this.sourceCountryList = this.store.select(selectSourceCountry);
  }

  onSourceCountryChange(event: {
    value: string;
    selectOptions: CountryInfo[] | null;
  }): void {
    this.getPartnerTransactionSettings();
    this.clearSelection(true);
    this.clearSelection(false);
    if (event.value !== '')
      this.store.dispatch(
        loadDestinationCountry({
          payload: { src_country: event.value },
        })
      );
    this.destCountryList = this.store.select(selectDestinationCountry);
    const selectedSourceCountry = getCountryInfoFromCountryCode(
      event.value,
      event.selectOptions
    );
    if (selectedSourceCountry)
      this.createTxnFrom.patchValue({
        senderDetails: {
          [this.attrConstant.mobileCode.key]: selectedSourceCountry.mobileCode,
        },
        [this.attrConstant.sourceCurrency.key]:
          selectedSourceCountry.currencySupported[0].currency,
      });
  }

  onDestCountryChange(event: {
    value: string;
    selectOptions: CountryInfo[] | null;
  }): void {
    this.getPartnerTransactionSettings();
    this.clearSelection(false);
    const selectedDestCountry = getCountryInfoFromCountryCode(
      event.value,
      event.selectOptions
    );
    if (selectedDestCountry)
      this.createTxnFrom.patchValue({
        beneDetails: {
          [this.attrConstant.beneMobileCode.key]:
            selectedDestCountry.mobileCode,
        },
        [this.attrConstant.destCurrency.key]:
          selectedDestCountry.currencySupported[0].currency,
      });
  }

  getPartnerTransactionSettings(): void {
    const formValue = this.createTxnFrom.value;
    if (
      formValue[this.attrConstant.sourceCountry.key] !== '' &&
      formValue[this.attrConstant.destCountry.key] !== ''
    )
      this.transactionSettingsService
        .getPartnerTransactionSettings({
          src_country: formValue[this.attrConstant.sourceCountry.key],
          dest_country: formValue[this.attrConstant.destCountry.key],
        })
        .pipe(takeUntil(this.#destroy$))
        .subscribe({
          next: x => {
            if (x.success) {
              this.transactionSettings = x.response;
            }
          },
        });
    else if (formValue[this.attrConstant.sourceCountry.key] === '')
      this.createTxnFrom.reset();
    else if (formValue[this.attrConstant.destCountry.key] === '')
      this.createTxnFrom.patchValue({
        [this.attrConstant.transferType.key]: '',
        [this.attrConstant.serviceLevel.key]: '',
        [this.attrConstant.sourceOfIncome.key]: '',
        [this.attrConstant.purpose.key]: '',
        [this.attrConstant.paymentType.key]: '',
      });
  }

  onKeyUp(type: string): void {
    if (type !== '') this.amountType = type;
    const payload = buildChargesPayload(this.createTxnFrom, this.amountType);
    if (
      payload.amount !== 0 &&
      payload.src_country !== '' &&
      payload.transfer_type_po !== '' &&
      !!payload.service_level &&
      payload.dest_country !== '' &&
      payload.payment_type !== ''
    ) {
      this.subject.next(payload);
    }
  }

  onChooseClick(isSender: boolean): void {
    const formValue = this.createTxnFrom.value;
    if (isSender && formValue[this.attrConstant.sourceCountry.key] === '') {
      this.messageHandlerService.show('Please select source country');
      return;
    }
    if (!isSender && formValue[this.attrConstant.destCountry.key] === '') {
      this.messageHandlerService.show('Please select destination country');
      return;
    }
    const dialog = this.dialogRef.open(ExistingUserComponent, {
      disableClose: true,
      data: {
        popupType: isSender,
        sourceCountry: formValue[this.attrConstant.sourceCountry.key],
        destinationCountry: formValue[this.attrConstant.destCountry.key],
      },
    });
    dialog
      .afterClosed()
      .subscribe((result: ExistingUserDialogResult | undefined) => {
        if (!result) return;
        if (result.isSender) {
          this.isExistingSender = Object.keys(result.data).length !== 0;
          this.selectedSenderId = result.data.sender_id ?? '';
          this.createTxnFrom.get('senderDetails')?.patchValue(result.data);
          if (this.isExistingSender)
            this.createTxnFrom.controls['senderDetails'].disable();
          else this.createTxnFrom.controls['senderDetails'].enable();
        } else {
          this.isExistingBeneficiary = Object.keys(result.data).length !== 0;
          this.selectedBeneficiaryId = result.data.bene_id ?? '';
          this.createTxnFrom.get('beneDetails')?.patchValue(result.data);
          if (this.isExistingBeneficiary)
            this.createTxnFrom.controls['beneDetails'].disable();
          else this.createTxnFrom.controls['beneDetails'].enable();
        }
      });
  }

  clearSelection(isSender: boolean): void {
    if (isSender) {
      this.isExistingSender = false;
      const senderDetailsControl = this.createTxnFrom.get('senderDetails');
      if (senderDetailsControl) {
        senderDetailsControl.enable();
        const mobileCodeControl = senderDetailsControl.get('mobile_code');
        if (mobileCodeControl) mobileCodeControl.disable();
        senderDetailsControl.reset();
      }
    } else {
      this.isExistingBeneficiary = false;
      const beneDetailsControl = this.createTxnFrom.get('beneDetails');
      if (beneDetailsControl) {
        beneDetailsControl.enable();
        const beneMobileCodeControl =
          beneDetailsControl.get('bene_mobile_code');
        if (beneMobileCodeControl) beneMobileCodeControl.disable();
        beneDetailsControl.reset();
      }
    }
  }

  calculate(payload: TransactionChargesInterface): void {
    this.transactionService
      .getTransactionCharges(payload)
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.createTxnFrom.patchValue({
              [this.attrConstant.sourceAmount.key]:
                this.amountType === 'source'
                  ? payload.amount
                  : x.response.source_amount,
              [this.attrConstant.destAmount.key]:
                this.amountType === 'source'
                  ? x.response.receive_amount
                  : payload.amount,
              [this.attrConstant.headOfficeFee.key]: x.response.platform_fee,
              [this.attrConstant.commission.key]: x.response.total_fees,
              [this.attrConstant.rate.key]: x.response.master_rate,
            });
          }
        },
      });
  }

  onCreateTransaction(): void {
    if (!this.createTxnFrom.valid) return;
    // Guard: confirm the velocity limit isn't exceeded before creating.
    this.deliveryLookup
      .checkVelocityLimit(buildVelocityPayload(this.createTxnFrom))
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: res => {
          if (res.success) this.submitTransaction();
          else this.messageHandlerService.show(res.message);
        },
      });
  }

  private submitTransaction(): void {
    const formValue = this.createTxnFrom.getRawValue();
    const transferType = formValue[this.attrConstant.transferType.key];
    const beneData = this.isExistingBeneficiary
      ? { beneficiary_id: this.selectedBeneficiaryId }
      : pruneDeliveryDetails(transferType, formValue.beneDetails);
    const senderData = this.isExistingSender
      ? { sender_id: this.selectedSenderId }
      : buildNewSenderPayload(formValue.senderDetails, value =>
          this.utils.convertDateFormat(value)
        );
    const payload = {
      ...stripCalculatedFields(formValue),
      ...beneData,
      ...senderData,
      amount_type: this.amountType,
      amount:
        this.amountType === 'source'
          ? formValue[this.attrConstant.sourceAmount.key]
          : formValue[this.attrConstant.destAmount.key],
      channel: 'web',
      created_by: this.userInfo.userName,
    };
    this.transactionService
      .createTransaction(payload as unknown as CreateTransactionInterface)
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.messageHandlerService.show(
              'Transactions created successfully'
            );
            this.router.navigate(['dashboard/transaction/list']);
          }
        },
      });
  }

  onChooseDocument(image: string): void {
    this.senderDetailsGroup.get(this.attrConstant.image.key)?.setValue(image);
  }

  patchPassword(password: string): void {
    this.senderDetailsGroup
      .get(this.attrConstant.password.key)
      ?.setValue(password);
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
